import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { BeeError, BeeRequestError, BeeResponseError } from './error'

import utils from 'axios/lib/utils'
import normalizeHeaderName from 'axios/lib/helpers/normalizeHeaderName'
axios.defaults.adapter = require('axios/lib/adapters/http') // https://stackoverflow.com/a/57320262

/**
 * Utility function that sets passed headers to ALL axios calls without distinction of Bee URLs.
 *
 * @param headers
 */
export function setDefaultHeaders(headers: Record<string, string>): void {
  axios.defaults.headers.common = headers
}

/**
 * Utility function from axios's implementation
 * https://github.com/axios/axios/blob/d99d5faac29899eba68ce671e6b3cbc9832e9ad8/lib/defaults.js
 *
 * @param headers
 * @param value
 */
function setContentTypeIfUnset(headers: Record<string, unknown>, value: string) {
  if (!utils.isUndefined(headers) && utils.isUndefined(headers['Content-Type'])) {
    headers['Content-Type'] = value
  }
}

/**
 * Taken over from axios's implementation
 * https://github.com/axios/axios/blob/d99d5faac29899eba68ce671e6b3cbc9832e9ad8/lib/defaults.js
 *
 * Unfortunately we can't use the default implementation as fallback one and override only the JSON serialization as the JSON.stringify
 * is a last thing to do as all the previous cases (buffer, file etc.) are objects as well
 * so they have to be ruled out first before trying JSON.stringify.
 *
 * @param data
 * @param headers
 */
function transformRequest(data: unknown, headers: Record<string, unknown>): string | unknown {
  normalizeHeaderName(headers, 'Accept')
  normalizeHeaderName(headers, 'Content-Type')

  if (
    utils.isFormData(data) ||
    utils.isArrayBuffer(data) ||
    utils.isBuffer(data) ||
    utils.isStream(data) ||
    utils.isFile(data) ||
    utils.isBlob(data)
  ) {
    return data
  }

  if (utils.isArrayBufferView(data)) {
    return data.buffer
  }

  if (utils.isURLSearchParams(data)) {
    setContentTypeIfUnset(headers, 'application/x-www-form-urlencoded;charset=utf-8')

    return data.toString()
  }

  if (utils.isObject(data) || (headers && headers['Content-Type'] === 'application/json')) {
    setContentTypeIfUnset(headers, 'application/json')

    return JSON.stringify(data)
  }

  return data
}

export type NodeApi = {
  id: string
  nodeName: string
  apiHost: string
  debugApiHost: string
  authorizationCode: string
}

export function getNodeApiList(): NodeApi[] {
  const nodeApi: NodeApi[] = []
  try {
    const arr: NodeApi[] = JSON.parse(localStorage.node_api)
    arr.forEach(({ id, nodeName, apiHost, debugApiHost, authorizationCode }, index) => {
      if (id) {
        const node: NodeApi = {
          id,
          nodeName: nodeName ? nodeName.toString().trim() : `node${index}`,
          apiHost: apiHost ? apiHost.toString().trim() : '',
          debugApiHost: debugApiHost ? debugApiHost.toString().trim() : '',
          authorizationCode: authorizationCode ? authorizationCode.toString().trim() : '',
        }

        nodeApi.push(node)
      }
    })
  } catch (_) {
    ///
  }

  return nodeApi
}

function getNodeApi(): NodeApi {
  let nodeApi: NodeApi = {
    id: '',
    nodeName: '',
    apiHost: process.env.REACT_APP_ANT_HOST || 'http://localhost:1633',
    debugApiHost: process.env.REACT_APP_ANT_DEBUG_HOST || 'http://localhost:1635',
    authorizationCode: '',
  }
  const nodeApiList: NodeApi[] = getNodeApiList()
  const id = localStorage.acitve_node_api_key || ''

  for (let i = 0; i < nodeApiList.length; i++) {
    if (nodeApiList[i].id === id) {
      nodeApi = nodeApiList[i]
      break
    }
  }

  if (nodeApiList.length > 0 && !nodeApi.id) {
    nodeApi = nodeApiList[0]
    localStorage.acitve_node_api_key = nodeApi.id
  }

  return nodeApi
}

export async function safeAxios<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
  try {
    const response = await axios({
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      transformRequest,
      ...config,
      headers: {
        ...config.headers,
        "Authorization": getNodeApi()?.authorizationCode || ""
      }
    })

    return response
  } catch (e) {
    if (e.response) {
      if (e.response.data?.message) {
        throw new BeeResponseError(e.response.status, `${e.response.statusText}: ${e.response.data.message}`)
      } else {
        throw new BeeResponseError(e.response.status, e.response.statusText)
      }
    } else if (e.request) {
      throw new BeeRequestError(e.message)
    } else {
      throw new BeeError(e.message)
    }
  }
}
